import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import HtmlEditorToolBar from "./HtmlEditorToolBar";

type HtmlEditorType = {
  readOnly?: boolean;
  body: string;
  onEditorUpdate: (body: string) => void;
  placeholder?: string;
};

type FormatState = {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
};

type HtmlInitPluginProps = {
  body: string;
  editorContainerRef: React.MutableRefObject<HTMLDivElement | null>;
};

const HtmlInitPlugin = ({ body, editorContainerRef }: HtmlInitPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const prevBodyRef = useRef<string>(body);

  useEffect(() => {
    if (prevBodyRef.current === body) {
      return;
    }
    // prevent losing focus after every character change
    if (editorContainerRef.current?.contains(document.activeElement)) {
      return;
    }
    prevBodyRef.current = body;
    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(body, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });
  }, [body, editor, editorContainerRef]);

  return null;
};

const HtmlEditor = ({
  readOnly,
  body,
  onEditorUpdate,
  placeholder = "Enter email"
}: HtmlEditorType) => {
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const [formatState, setFormatState] = useState<FormatState>({
    isBold: false,
    isItalic: false,
    isUnderline: false
  });

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        onEditorUpdate(html);
      });
    },
    [onEditorUpdate]
  );

  const handleSelectionChange = useCallback((editorState: EditorState) => {
    editorState.read(() => {
      const selection = editorState._selection;
      if (selection && "hasFormat" in selection) {
        setFormatState({
          isBold: (selection as any).hasFormat("bold"),
          isItalic: (selection as any).hasFormat("italic"),
          isUnderline: (selection as any).hasFormat("underline")
        });
      }
    });
  }, []);

  const initialConfig = {
    namespace: "HtmlEditor",
    onError: (error: Error) => console.error(error),
    editable: !readOnly
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        ref={editorContainerRef}
        className="HTMLEditorContainer h-full flex flex-col flex-1 overflow-visible"
      >
        <div className="ToolBarContainer">
          <HtmlEditorToolBar
            isBold={formatState.isBold}
            isItalic={formatState.isItalic}
            isUnderline={formatState.isUnderline}
            editorContainerRef={editorContainerRef}
          />
        </div>
        <div className="EditorContainer flex flex-col grow min-h-[150px] border-2 border-solid border-[var(--FooterGridBackground)] rounded-sm m-0.5 focus-within:outline-2 focus-within:outline-[hsl(var(--ring))] focus-within:border-transparent">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="LexicalContentEditable flex-1 min-h-[150px] p-2 outline-none relative"
                data-placeholder={placeholder}
              />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <OnChangePlugin
          onChange={(editorState, editor) => {
            handleChange(editorState, editor);
            handleSelectionChange(editorState);
          }}
        />
        <HtmlInitPlugin body={body} editorContainerRef={editorContainerRef} />
        {!readOnly && <AutoFocusPlugin />}
      </div>
    </LexicalComposer>
  );
};

export default React.memo<HtmlEditorType>(HtmlEditor);
